import React from "react"
import { Box, Tooltip, type BoxProps } from "@mui/material"
import { composeSx } from "../theme/composeSx"
import { resolveViraPalette } from "../theme/resolveViraPalette"
import { Heading, ViraText } from "../atoms/ViraText"
import { ViraButton } from "../atoms/ViraButton"

const DEFAULT_APPROVE_TOOLTIP =
  "Add this nudge to the client's action plan."
const DEFAULT_REJECT_TOOLTIP =
  "Leave this suggestion out — it will not be added to the plan."

export type TechniqueListItemProps = BoxProps & {
  title: string
  detail?: string
  /** When the nudge will be delivered (one-time / weekly). */
  cadence?: string
  approved?: boolean
  rejected?: boolean
  disabled?: boolean
  /** Pending suggestion: primary accept action (API accept). Default: Add */
  onApprove?: () => void
  /** Pending suggestion: decline action (API reject). Default: Leave out */
  onReject?: () => void
  /** After add: open customize (goal / schedule). Also available while pending. */
  onCustomize?: () => void
  /** After add: remove from plan (API reject). */
  onRemove?: () => void
  approveLabel?: string
  rejectLabel?: string
  customizeLabel?: string
  removeLabel?: string
  approvedStatusLabel?: string
  rejectedStatusLabel?: string
  /** Hover help for the Add / Approve control. */
  approveTooltip?: string
  /** Hover help for the Leave out / Reject control. */
  rejectTooltip?: string
  /** Extra content under the row (goal editor, contingencies). */
  children?: React.ReactNode
}

/**
 * Single action-plan row with add/leave-out or customize/remove actions.
 * Safe under legacy MUI theme (no `ViraThemeProvider`).
 */
export function TechniqueListItem({
  title,
  detail,
  cadence,
  approved = false,
  rejected = false,
  disabled = false,
  onApprove,
  onReject,
  onCustomize,
  onRemove,
  approveLabel = "Add",
  rejectLabel = "Leave out",
  customizeLabel = "Customize",
  removeLabel = "Remove",
  approvedStatusLabel = "Added",
  rejectedStatusLabel = "Left out",
  approveTooltip = DEFAULT_APPROVE_TOOLTIP,
  rejectTooltip = DEFAULT_REJECT_TOOLTIP,
  children,
  sx,
  ...rest
}: TechniqueListItemProps) {
  const showPendingActions = !approved && !rejected && (onApprove || onReject)
  const showAddedActions = approved && (onCustomize || onRemove)
  /** Pending: keep Customize out of the Add / Leave out pair. */
  const showPendingCustomize = showPendingActions && Boolean(onCustomize)

  return (
    <Box
      sx={composeSx((theme) => {
        const vira = resolveViraPalette(theme)
        return {
          // Nested list row — not a second ViraCard. Softer inset, smaller radius.
          p: 2,
          borderRadius: "6px",
          border: `1px solid ${vira.softBorder}`,
          backgroundColor: vira.softSurface,
          display: "flex",
          flexDirection: "column",
          gap: 1.5,
          opacity: disabled ? 0.5 : 1,
          maxWidth: "100%",
        }
      }, sx)}
      {...rest}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          justifyContent: "space-between",
          alignItems: { xs: "stretch", sm: "flex-start" },
          gap: { xs: 1.5, sm: 2 },
        }}
      >
        <Box sx={{ minWidth: 0, maxWidth: "100%", flex: "1 1 auto" }}>
          <Heading level="section" sx={{ fontSize: 14, fontFamily: "inherit" }}>
            {title}
          </Heading>
          {detail ? (
            <ViraText tone="subtle" sx={{ fontSize: 12.5 }}>
              {detail}
            </ViraText>
          ) : null}
          {cadence ? (
            <ViraText
              tone="muted"
              sx={{ fontSize: 12, fontWeight: 600, mt: 0.5 }}
            >
              {cadence}
            </ViraText>
          ) : null}
          {showPendingCustomize ? (
            <ViraButton
              variant="ghost"
              size="sm"
              disabled={disabled}
              onClick={onCustomize}
              sx={{
                mt: 0.5,
                px: 0,
                minWidth: 0,
                alignSelf: "flex-start",
                whiteSpace: "nowrap",
              }}
            >
              {customizeLabel}
            </ViraButton>
          ) : null}
        </Box>

        {rejected && !onRemove ? (
          <ViraText
            tone="subtle"
            sx={{ fontSize: 12, flexShrink: 0, pt: { sm: 0.25 } }}
          >
            {rejectedStatusLabel}
          </ViraText>
        ) : showAddedActions ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              flexWrap: "wrap",
              gap: 1,
              alignItems: "center",
              flexShrink: 0,
              justifyContent: { xs: "flex-start", sm: "flex-end" },
            }}
          >
            {onCustomize ? (
              <ViraButton
                variant="secondary"
                size="sm"
                disabled={disabled}
                onClick={onCustomize}
                sx={{ whiteSpace: "nowrap" }}
              >
                {customizeLabel}
              </ViraButton>
            ) : null}
            {onRemove ? (
              <ViraButton
                variant="ghost"
                size="sm"
                disabled={disabled}
                onClick={onRemove}
                sx={{ whiteSpace: "nowrap", px: 1 }}
              >
                {removeLabel}
              </ViraButton>
            ) : null}
          </Box>
        ) : approved ? (
          <ViraText
            tone="positive"
            sx={{ fontSize: 12, flexShrink: 0, pt: { sm: 0.25 } }}
          >
            {approvedStatusLabel}
          </ViraText>
        ) : showPendingActions ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              flexWrap: "nowrap",
              gap: 1.25,
              alignItems: "center",
              flexShrink: 0,
              justifyContent: { xs: "flex-start", sm: "flex-end" },
            }}
          >
            {onApprove ? (
              <Tooltip
                title={approveTooltip}
                enterDelay={400}
                describeChild
              >
                <span>
                  <ViraButton
                    variant="secondary"
                    size="sm"
                    disabled={disabled}
                    onClick={onApprove}
                    sx={{ whiteSpace: "nowrap", flexShrink: 0 }}
                  >
                    {approveLabel}
                  </ViraButton>
                </span>
              </Tooltip>
            ) : null}
            {onReject ? (
              <Tooltip title={rejectTooltip} enterDelay={400} describeChild>
                <span>
                  <ViraButton
                    variant="ghost"
                    size="sm"
                    disabled={disabled}
                    onClick={onReject}
                    sx={{ whiteSpace: "nowrap", flexShrink: 0, px: 1 }}
                  >
                    {rejectLabel}
                  </ViraButton>
                </span>
              </Tooltip>
            ) : null}
          </Box>
        ) : null}
      </Box>
      {children ? <Box sx={{ width: "100%" }}>{children}</Box> : null}
    </Box>
  )
}
